// P3-026: Combo counter animation — staged cascade chain display
// Shows "N CHAIN" / "N CHAIN+" / "N CHAIN MAX" based on chainDepth and weightTier

import { useEffect, useState, useRef } from 'react';
import { EventBus } from '@/shared/game/EventBus';

export function ComboDisplay() {
  const [chainDepth, setChainDepth] = useState(0);
  const [weightTier, setWeightTier] = useState(0);
  const [comboMultiplier, setComboMultiplier] = useState(1.0);
  const [visible, setVisible] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [pulse, setPulse] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleCascadeRound = (data: {
      chainDepth: number;
      roundWeight: number;
      weightTier: number;
      comboMultiplier: number;
      roundDamage: number;
      isCrit: boolean;
    }) => {
      setChainDepth(data.chainDepth);
      setWeightTier(data.weightTier);
      setComboMultiplier(data.comboMultiplier);

      // Clear any pending fade timer
      if (fadeTimerRef.current !== null) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }

      // Show combo display if chainDepth >= 2 OR weightTier >= 1
      if (data.chainDepth >= 2 || data.weightTier >= 1) {
        setVisible(true);
        setPulse(true);
        setTimeout(() => setPulse(false), 200);
      }

      // Celebration for high combos
      if (data.chainDepth >= 3 || data.comboMultiplier >= 2.0) {
        setCelebrating(true);
      }
    };

    const handleTurnEnd = () => {
      if (fadeTimerRef.current !== null) {
        clearTimeout(fadeTimerRef.current);
      }
      fadeTimerRef.current = setTimeout(() => {
        setVisible(false);
        setChainDepth(0);
        setWeightTier(0);
        setComboMultiplier(1.0);
        setCelebrating(false);
      }, 1500);
    };

    EventBus.on('cascadeRoundComplete', handleCascadeRound);
    EventBus.on('turnEnd', handleTurnEnd);

    return () => {
      EventBus.off('cascadeRoundComplete', handleCascadeRound);
      EventBus.off('turnEnd', handleTurnEnd);
      if (fadeTimerRef.current !== null) {
        clearTimeout(fadeTimerRef.current);
      }
    };
  }, []);

  if (!visible) return null;

  const getDisplayText = (): string => {
    if (chainDepth === 1 && weightTier === 0) return '';
    if (chainDepth >= 4) return `${chainDepth} CHAIN MAX`;
    const plusSuffix = weightTier >= 2 ? '++' : weightTier === 1 ? '+' : '';
    return chainDepth >= 2
      ? `${chainDepth} CHAIN${plusSuffix}`
      : `CHAIN START${plusSuffix}`;
  };

  const displayText = getDisplayText();
  if (!displayText) return null;

  const getTierClass = (): string => {
    if (chainDepth >= 4) return 'combo-display__text--ultra';
    if (chainDepth >= 3) return 'combo-display__text--mega';
    if (weightTier >= 1) return 'combo-display__text--chain-plus';
    if (chainDepth >= 2) return 'combo-display__text--chain';
    return '';
  };

  return (
    <div className="combo-display">
      <div
        className={`combo-display__text ${pulse ? 'combo-display__text--pulse' : ''} ${getTierClass()} ${
          celebrating ? 'combo-display__text--celebrating' : ''
        }`}
      >
        {displayText}
        <span className="combo-display__multiplier">
          {'\u00d7'}{comboMultiplier.toFixed(2)}
        </span>
        {chainDepth >= 4 && <span className="combo-display__stars">{'\u2605\u2605\u2605'}</span>}
      </div>
      {celebrating && (
        <div className="combo-display__celebration">
          <div className="combo-display__particle combo-display__particle--1" />
          <div className="combo-display__particle combo-display__particle--2" />
          <div className="combo-display__particle combo-display__particle--3" />
          <div className="combo-display__particle combo-display__particle--4" />
        </div>
      )}
    </div>
  );
}
